import { apiClient } from './client';
import type {
  AccountQuery,
  AdsAccount,
  AppBinding,
  AccountTransaction,
  PageData,
  AssignOperatorReq,
  RechargeReq,
  AddBindingReq,
  ReportConsumeReq,
  TransactionQuery,
  CreateAccountReq,
  OperatorUser,
  AccountStats,
  SwitchBindingReq,
  AppPackageVO,
} from './types';

function normalizeAccount(account: AdsAccount): AdsAccount {
  const totalSpend = account.totalSpend ?? account.totalConsume ?? '0';
  return {
    ...account,
    name: account.name ?? account.accountName ?? null,
    accountName: account.accountName ?? account.name ?? null,
    totalSpend,
    totalConsume: account.totalConsume ?? totalSpend,
    banDate: account.banDate || null,
  };
}

function normalizeBinding(binding: AppBinding): AppBinding {
  return {
    ...binding,
    pkg: binding.pkg ?? binding.appPackage ?? binding.app ?? '',
    app: binding.app ?? binding.pkg ?? binding.appPackage,
    assignedOperatorId: binding.assignedOperatorId ?? binding.operatorId,
    appAvailable: binding.appAvailable ?? true,
  };
}

function normalizeTransaction(tx: AccountTransaction): AccountTransaction {
  return {
    ...tx,
    pkg: tx.pkg ?? tx.appPackage ?? tx.app ?? null,
    app: tx.app ?? tx.pkg ?? tx.appPackage,
    reversed: tx.reversed ?? false,
    reversalOfId: tx.reversalOfId ?? null,
  };
}

function normalizeAccountPage(page: PageData<AdsAccount>): PageData<AdsAccount> {
  return { ...page, records: page.records?.map(normalizeAccount) ?? [] };
}

function normalizeTransactionPage(
  page: PageData<AccountTransaction>,
): PageData<AccountTransaction> {
  return { ...page, records: page.records?.map(normalizeTransaction) ?? [] };
}

export async function listAccounts(query: AccountQuery): Promise<PageData<AdsAccount>> {
  const params = {
    pageNo: query.pageNo,
    pageSize: query.pageSize,
    platformId: query.platformId,
    providerId: query.providerId,
    accountCode: query.accountCode,
    accountName: query.accountName,
    status: query.status,
    operatorId: query.operatorId,
    pkg: query.pkg,
    createTimeStart: query.createTimeStart,
    createTimeEnd: query.createTimeEnd,
    updateTimeStart: query.updateTimeStart,
    updateTimeEnd: query.updateTimeEnd,
  };
  const res = await apiClient.get<PageData<AdsAccount>>('/ads-admin/ad-accounts', { params });
  return normalizeAccountPage(res.data);
}

export async function getAccount(accountId: string): Promise<AdsAccount> {
  const res = await apiClient.get<AdsAccount>(`/ads-admin/ad-accounts/${accountId}`);
  return normalizeAccount(res.data);
}

export async function assignOperator(accountId: string, req: AssignOperatorReq): Promise<void> {
  await apiClient.put(`/ads-admin/ad-accounts/${accountId}/operator`, req);
}

export async function clearOperatorAssignment(accountId: string): Promise<void> {
  await apiClient.delete(`/ads-admin/ad-accounts/${accountId}/operator`);
}

export async function rechargeAccount(accountId: string, req: RechargeReq): Promise<void> {
  await apiClient.post(`/ads-admin/ad-accounts/${accountId}/recharge`, req);
}

export async function zeroAccount(accountId: string): Promise<void> {
  await apiClient.post(`/ads-admin/ad-accounts/${accountId}/zero`);
}

export async function banAccount(accountId: string): Promise<void> {
  await apiClient.post(`/ads-admin/ad-accounts/${accountId}/ban`);
}

export async function listBindings(accountId: string): Promise<AppBinding[]> {
  const res = await apiClient.get<PageData<AppBinding>>(
    `/ads-admin/ad-accounts/${accountId}/bindings`,
    {
      params: { pageSize: 100 },
    },
  );
  return res.data.records?.map(normalizeBinding) ?? [];
}

export async function addBinding(accountId: string, req: AddBindingReq): Promise<void> {
  await apiClient.post(`/ads-admin/ad-accounts/${accountId}/bindings`, req);
}

export async function switchBinding(accountId: string, req: SwitchBindingReq): Promise<void> {
  await apiClient.post(`/ads-admin/ad-accounts/${accountId}/bindings/switch`, req);
}

export async function endBinding(accountId: string, bindingId: number): Promise<void> {
  await apiClient.delete(`/ads-admin/ad-accounts/${accountId}/bindings/${bindingId}`);
}

export async function listTransactions(
  accountId: string,
  query: TransactionQuery,
): Promise<PageData<AccountTransaction>> {
  const res = await apiClient.get<PageData<AccountTransaction>>(
    `/ads-admin/ad-accounts/${accountId}/transactions`,
    { params: query },
  );
  return normalizeTransactionPage(res.data);
}

export async function reportConsume(accountId: string, req: ReportConsumeReq): Promise<void> {
  await apiClient.post(`/ads-admin/ad-accounts/${accountId}/transactions`, req);
}

export async function createAccount(req: CreateAccountReq): Promise<AdsAccount> {
  const res = await apiClient.post<AdsAccount>('/ads-admin/ad-accounts', {
    ...req,
    accountName: req.accountName ?? req.name,
  });
  return normalizeAccount(res.data);
}

export async function fetchOperators(): Promise<OperatorUser[]> {
  const res = await apiClient.get<PageData<OperatorUser>>('/ads-admin/operators', {
    params: { pageNo: 1, pageSize: 100 },
  });
  return res.data.records ?? [];
}

export async function getAccountStats(): Promise<AccountStats> {
  const res = await apiClient.get<AccountStats>('/ads-admin/ad-accounts/stats');
  const totalSpend = res.data.totalSpend ?? res.data.totalConsume ?? '0';
  return { ...res.data, totalSpend, totalConsume: res.data.totalConsume ?? totalSpend };
}

export interface UpdateAccountDto {
  accountCode?: string;
  accountName?: string;
  platformId?: number;
  providerId?: number;
  /** @deprecated use accountName */
  name?: string;
  /** @deprecated use providerId */
  providerName?: string;
  remark?: string;
  status?: 0 | 1;
}

export async function updateAccount(accountId: string, dto: UpdateAccountDto): Promise<void> {
  await apiClient.put(`/ads-admin/ad-accounts/${accountId}`, {
    ...dto,
    accountName: dto.accountName ?? dto.name,
  });
}

export async function listAccountApps(): Promise<AppPackageVO[]> {
  const res = await apiClient.get<AppPackageVO[]>('/ads-admin/ad-accounts/apps');
  return res.data;
}

export interface DailySpendResult {
  bizDate: string;
  totalSpend: string;
  yesterdaySpend?: string;
}

interface DailySpendRaw {
  date: string;
  dailySpend: string | number | null;
  yesterdaySpend?: string | number | null;
}

export async function getDailySpend(bizDate: string): Promise<DailySpendResult> {
  const res = await apiClient.get<DailySpendRaw>('/ads-admin/dashboard/daily-spend', {
    params: { date: bizDate },
  });
  const raw = res.data;
  return {
    bizDate: raw.date ?? bizDate,
    totalSpend: raw.dailySpend == null ? '0' : String(raw.dailySpend),
    yesterdaySpend: raw.yesterdaySpend == null ? undefined : String(raw.yesterdaySpend),
  };
}

export interface BatchCreateAccountsReq {
  platformId: number;
  providerId: number;
  assignedOperatorId?: number;
  accounts: { accountCode: string; accountName: string; initialAmount?: string }[];
}

export interface BatchCreateResultItem {
  accountCode: string;
  accountName: string;
  status: 'created' | 'failed';
  error: string | null;
}

interface BatchCreateRawResp {
  created: unknown[];
  errors: Array<{ index: number; reason: string }>;
}

export async function batchCreateAccounts(
  req: BatchCreateAccountsReq,
): Promise<BatchCreateResultItem[]> {
  const res = await apiClient.post<BatchCreateRawResp | BatchCreateResultItem[]>(
    '/ads-admin/ad-accounts/batch',
    req,
  );
  if (Array.isArray(res.data)) return res.data as BatchCreateResultItem[];
  // Adapt backend { created, errors } → per-item result using original req.accounts
  const raw = res.data as BatchCreateRawResp;
  const errorMap = new Map<number, string>(
    (raw.errors ?? []).map((e) => [e.index, e.reason ?? '创建失败']),
  );
  return req.accounts.map((item, i) => ({
    accountCode: item.accountCode,
    accountName: item.accountName,
    status: errorMap.has(i) ? 'failed' : 'created',
    error: errorMap.get(i) ?? null,
  }));
}

export interface BatchAccountStatusResult {
  succeeded: string[];
  errors: { accountId: string; reason: string }[];
}

/** 单次批量改状态的账户数上限，与后端 BatchBanAccountReq/BatchZeroAccountReq 的 @Size(max = 50) 对应。 */
export const MAX_BATCH_SIZE = 50;

// 逐户串行执行，50 个户可能超过 apiClient 默认的 20s
const BATCH_TIMEOUT_MS = 60_000;

function normalizeBatchResult(raw: Partial<BatchAccountStatusResult>): BatchAccountStatusResult {
  return { succeeded: raw.succeeded ?? [], errors: raw.errors ?? [] };
}

export async function batchBanAccounts(req: {
  accountIds: string[];
  banDate?: string;
}): Promise<BatchAccountStatusResult> {
  const res = await apiClient.post<BatchAccountStatusResult>(
    '/ads-admin/ad-accounts/batch-ban',
    req,
    { timeout: BATCH_TIMEOUT_MS },
  );
  return normalizeBatchResult(res.data);
}

export async function batchZeroAccounts(req: {
  accountIds: string[];
}): Promise<BatchAccountStatusResult> {
  const res = await apiClient.post<BatchAccountStatusResult>(
    '/ads-admin/ad-accounts/batch-zero',
    req,
    { timeout: BATCH_TIMEOUT_MS },
  );
  return normalizeBatchResult(res.data);
}

export async function checkAccountCodes(accountCodes: string[]): Promise<string[]> {
  const res = await apiClient.post<{ existingCodes: string[] }>(
    '/ads-admin/ad-accounts/check-codes',
    { accountCodes },
  );
  return res.data.existingCodes ?? [];
}
